import { isAxiosError } from 'axios';
import { useCallback, useRef, useState } from 'react';

import { useGetAddressByPostalCode } from '@/api/hooks';
import { type PostalCodeAddress } from '@/types/location';
import { useTranslate } from '@/state/i18n';
import { normalizeApiError } from '@/utils/error';

interface UsePostalCodeLookupResult {
  isLoading: boolean;
  error: string | null;
  lastResolvedPostalCode: string | null;
  lookupPostalCode: (postalCode: string) => Promise<PostalCodeAddress | null>;
}

/**
 * Copied from bodyprint-web `hooks/usePostalCodeLookup.ts`, with one fix. The API instance rejects
 * with an `AppApiError`, never an axios error, so the web copy always fell into the generic
 * message. Here the status comes from `normalizeApiError` and "reached the API" from its `raw`.
 */
export function usePostalCodeLookup(): UsePostalCodeLookupResult {
  const t = useTranslate('hooks.usePostalCodeLookup');
  const [error, setError] = useState<string | null>(null);
  const [lastResolvedPostalCode, setLastResolvedPostalCode] = useState<string | null>(null);

  const lastRequestedPostalCodeRef = useRef<string | null>(null);
  const { mutateAsync, isPending } = useGetAddressByPostalCode();

  const lookupPostalCode = useCallback(
    async (postalCode: string): Promise<PostalCodeAddress | null> => {
      const digits = postalCode.replace(/\D/g, '');

      if (digits.length !== 8) {
        setError(null);
        return null;
      }

      if (digits === lastResolvedPostalCode?.replace(/\D/g, '')) {
        return null;
      }

      lastRequestedPostalCodeRef.current = digits;
      setError(null);

      try {
        const data = await mutateAsync(digits);

        if (lastRequestedPostalCodeRef.current !== digits) return null;

        setLastResolvedPostalCode(digits);
        return data;
      } catch (err) {
        if (lastRequestedPostalCodeRef.current !== digits) return null;

        const { status, raw } = normalizeApiError(err);
        if (status === 404) setError(t('notFound'));
        else if (status === 400) setError(t('invalid'));
        else if (isAxiosError(raw)) setError(t('unavailable'));
        else setError(t('genericError'));

        return null;
      }
    },
    [lastResolvedPostalCode, mutateAsync, t],
  );

  return { isLoading: isPending, error, lastResolvedPostalCode, lookupPostalCode };
}
